import { create } from 'zustand';
import { ROLE_DISPLAY_NAMES, isPrivilegedRole, type UserRole } from '@pg/shared';
import { api } from '../services/api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  companyId: string;
  lastLoginAt: string | null;
}

// Friendly label for a role (SENIOR_ESTIMATOR → "Lead Estimator", etc.).
export function displayRole(role: UserRole | undefined | null): string {
  if (!role) return '';
  return ROLE_DISPLAY_NAMES[role] ?? role;
}

// Selector helpers for components.
export function useIsAdmin(): boolean {
  return useAuthStore((s) => s.user?.role === 'ADMIN');
}
export function useIsLeadOrAdmin(): boolean {
  return useAuthStore((s) => !!s.user && isPrivilegedRole(s.user.role));
}
export function useCurrentUser(): AuthUser | null {
  return useAuthStore((s) => s.user);
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  hydrated: false,

  hydrate: async () => {
    set({ loading: true });
    try {
      const res = await api.get<{ user: AuthUser | null }>('/auth/me');
      set({ user: res.user, hydrated: true, loading: false });
    } catch {
      set({ user: null, hydrated: true, loading: false });
    }
  },

  login: async (email, password) => {
    set({ loading: true });
    const res = await api.post<{ user: AuthUser }>('/auth/login', { email, password });
    set({ user: res.user, loading: false });
  },

  logout: async () => {
    await api.post('/auth/logout');
    set({ user: null });
  },
}));
